import { Button, Flex } from '@cognite/cogs.js';
import { Form, Modal } from 'antd';
import { Icon } from 'components/Icon';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { URL_DOCS } from 'utils/config';
import { OIDCLogin } from './OIDCLogin';

export const LoginPage = () => {
  const [appDevInfoPopup, setAppDevInfoPopup] = useState(false);
  const [infoManagerInfoPopup, setInfoManagerInfoPopup] = useState(false);

  return (
    <div style={{ overflow: 'auto', scrollBehavior: 'smooth' }}>
      <LoginPageContainer>
        <Header>
          {/* Graphic not available in COGS v9 → replaced with Icon(Cognite) */}
          <Icon type="Cognite" className="white-logo" />
          <a href={URL_DOCS} target="_blank" rel="noreferrer">
            <ViewDocumentation>
              <Icon type="ExternalLink" />
              &nbsp; View full documentation
            </ViewDocumentation>
          </a>
        </Header>
        <LoginWrapper>
          <div className="content">
            <LoginContent>
              <div>
                <Title style={{ color: 'white' }}>Welcome to CDF EPC!</Title>
                <Description>
                  <strong>Cognite Data Fusion EPC Extension</strong> is a
                  platform for building and governing the digital backbone of
                  the next generation of innovative capital projects. It enables
                  project teams to manage the creation and use of reliable data
                  and facilitate seamless collaboration between partners,
                  vendors, and service providers. Create custom solutions and
                  deploy flexible applications running on the digital twin.
                </Description>
              </div>
              <Form>
                <OIDCLogin />
              </Form>
            </LoginContent>
          </div>
        </LoginWrapper>
        <a href="#intro">
          <IntroPageSegue>
            <div>What is CDF EPC?</div>
            <Icon type="ChevronDownSmall" style={{ marginTop: '0.5rem' }} />
          </IntroPageSegue>
        </a>
      </LoginPageContainer>
      <ProductIntroPage id="intro">
        <Title>What is CDF EPC?</Title>
        <ProductIntroWrapper>
          <div />
          <StyledIllustration />
          <InfoBoxWrapper>
            <InfoBox
              title="Application Developer"
              url="/images/illustrations/AppDev.svg"
              onClick={() => setAppDevInfoPopup(true)}
            />
            <InfoBox
              title="Information Manager"
              url="/images/illustrations/InfoManager.svg"
              onClick={() => setInfoManagerInfoPopup(true)}
            />
          </InfoBoxWrapper>
        </ProductIntroWrapper>
      </ProductIntroPage>
      {appDevInfoPopup && (
        <InfoBoxDetailedPopup
          title="Enabling Application Developers to:"
          description="Build, deploy and maintain complex industrial applications running on the Digital Twin."
          url="/images/illustrations/AppDev.svg"
          points={[
            'Get a feature rich backend to define data models',
            'Access managed API endpoints with critical features like aggregates, search, filtering',
            'Protect and monitor the data journey from the Digital Twin to the application and back',
          ]}
          onClose={() => setAppDevInfoPopup(false)}
        />
      )}
      {infoManagerInfoPopup && (
        <InfoBoxDetailedPopup
          title="Enabling Information Managers to:"
          description="Manage and govern all data and applications in the Digital Twin."
          url="/images/illustrations/InfoManager.svg"
          points={[
            'Govern data quality and lineage, manage access, and track project progress',
            'Empower collaboration across the value chain in the creation and use of data',
          ]}
          onClose={() => setInfoManagerInfoPopup(false)}
        />
      )}
    </div>
  );
};

const InfoBox = ({
  title,
  url,
  onClick,
}: {
  title: string;
  url: string;
  onClick: VoidFunction;
}) => (
  <StyledInfoBox url={url} onClick={onClick}>
    <ArrowUpRight type="ArrowUpRight" />
    <InfoBoxTitle>{title}</InfoBoxTitle>
  </StyledInfoBox>
);

const InfoBoxDetailedPopup = ({
  title,
  description,
  url,
  points,
  onClose,
}: {
  title: string;
  description: string;
  url: string;
  points: string[];
  onClose: VoidFunction;
}) => (
  <Modal
    visible
    footer={null}
    onCancel={onClose}
    maskClosable
    width="30rem"
    centered
  >
    <Flex style={{ marginBottom: '2rem' }}>
      <div>
        <h1 style={{ fontSize: '1rem', fontWeight: 600 }}>{title}</h1>
        <p style={{ fontSize: '0.75rem', marginTop: '1.375rem' }}>
          {description}
        </p>
      </div>
      <StyledPopupIllustration url={url} />
    </Flex>
    <div>
      {points.map((point, index) => (
        <StyledListItems key={point}>
          <div>
            <span>{index + 1}</span>
          </div>
          <span>{point}</span>
        </StyledListItems>
      ))}
    </div>

    <div style={{ textAlign: 'right', marginTop: '2rem' }}>
      <Button type="primary" onClick={onClose}>
        Close
      </Button>
    </div>
  </Modal>
);

const LoginPageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background-image: url('/images/login-screen.png');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 103% 103%;
`;

const LoginWrapper = styled.div`
  width: 100%;
  color: #ffffff;
  background: #1b21409a;
  margin: auto;
  padding: 50px 120px;

  .content {
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
  }
`;

const LoginContent = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  align-items: center;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 2.25rem;
  margin-bottom: 1.25rem;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.5rem;
`;

const Header = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 80px;
  padding-bottom: 0;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const ViewDocumentation = styled.div`
  color: #ffffff;
  font-size: 1rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover > i {
    color: var(--cogs-primary);
  }
`;

const jump = keyframes`
   0% {
     transform: translateY(0)
   }
   100% {
     transform: translateY(15px)
   }
 `;

const IntroPageSegue = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  cursor: pointer;
  text-align: center;
  color: #ffffff;
  padding-bottom: 3.5rem;
  font-size: 1rem;
  &:hover > i {
    animation: 0.5s linear infinite alternate ${jump};
  }
`;

const ProductIntroPage = styled.div`
  max-width: 1200px;
  margin: auto;
  height: 100vh;
  padding: 4rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProductIntroWrapper = styled.div`
  display: grid;
  grid-template-columns: 25% 50% 25%;
  align-items: center;
  position: relative;
  width: 100%;
`;

const StyledIllustration = styled.div`
  width: 100%;
  height: calc(100vh - 80px);
  margin-left: -4rem;
  background-image: url('/images/illustrations/EPC.svg');
  background-repeat: no-repeat;
  background-position: center;
  position: relative;
  background-size: auto auto;
  @media (min-width: 640px) {
    background-size: 400px;
  }
  @media (min-width: 768px) {
    background-size: 450px;
    margin-left: -6rem;
  }
  @media (min-width: 1024px) {
    background-size: 500px;
  }
`;

const InfoBoxWrapper = styled.div`
  margin-top: -18rem;
  width: 100%;
  margin-left: -1rem;
  display: none;
  @media (min-width: 768px) {
    display: block;
    margin-left: -4rem;
  }
  @media (min-width: 1024px) {
    display: block;
    margin-left: -6rem;
  }
`;

interface BGImage {
  url: string;
}

const StyledInfoBox = styled.div<BGImage>`
  width: 268px;
  height: 131px;
  cursor: pointer;
  background-color: #ffffff;
  background-image: ${({ url }: BGImage) => (url ? `url(${url})` : 'none')};
  background-repeat: no-repeat;
  background-position-y: center;
  background-position-x: 1rem;
  background-size: 6.25rem;
  border: 1px solid #bfbfbf;
  box-sizing: border-box;
  box-shadow: 0px 0px 6px 2px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  transition: all 0.5s ease-in-out;
  position: relative;
  &:not(:last-child) {
    margin-bottom: 5rem;
  }
  &:before {
    content: url('/images/illustrations/bracket.svg');
    position: absolute;
    left: -24px;
    top: -20px;
  }
  &:hover {
    box-shadow: 0px 0px 10px 5px rgba(0, 0, 0, 0.1);
    border-color: var(--cogs-primary);
    i {
      color: var(--cogs-primary);
    }
  }
`;

const ArrowUpRight = styled(Icon)`
  position: absolute;
  top: 0;
  right: 0;
  margin: 0.5rem;
  color: var(--cogs-greyscale-grey6);
`;

const InfoBoxTitle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  left: 150px;
  font-weight: 500;
  color: var(--cogs-greyscale-grey9);
`;

const StyledPopupIllustration = styled.div<BGImage>`
  background-image: ${({ url }: BGImage) => (url ? `url(${url})` : 'none')};
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100px;
  width: 250px;
  height: 110px;
`;

const StyledListItems = styled.div`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  font-size: 12px;
  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }
  div {
    span {
      border: 1px solid var(--cogs-primary);
      color: var(--cogs-primary);
      border-radius: 50%;
      height: 30px;
      width: 30px;
      line-height: 30px;
      text-align: center;
    }
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
